import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/custom-select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { getTemplateName, STORY_TEMPLATES, StoryTemplate } from '@/lib/storyTemplates';
import { templateService } from '@/services/template';
import React, { useState } from 'react';
import { TimelineDetails, TimelinePreview } from './TimeLinePreview';

interface TemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timelineId: number;
  plotLineId?: number;
  onApplied?: () => void;
}

const convertStoryTemplateToTimelineDetails = (template: StoryTemplate): TimelineDetails => {
  return {
    name: template.name,
    description: template.description,
    beats: template.beats,
    recommendedChapters: template.recommendedChapters
  };
};

export const TemplateDialog: React.FC<TemplateDialogProps> = ({
  open,
  onOpenChange,
  timelineId,
  onApplied
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const { toast } = useToast();

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      setIsApplying(true);
      console.log(`Applying template ${selectedTemplate} to timeline ${timelineId}`);

      const result = await templateService.applyTemplateToExistingTimeline(
        selectedTemplate,
        timelineId
      );

      console.log('Template application result:', result);

      if (result.success) {
        toast({
          title: 'Template Applied',
          description: `Successfully applied "${getTemplateName(selectedTemplate)}" template to your timeline.`,
        });

        onOpenChange(false);
        if (onApplied) onApplied();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to apply template. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error applying template:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to apply template. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
          <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
            <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">Apply Story Template</DialogTitle>
            <DialogDescription className="text-base text-muted-foreground leading-relaxed">
              Select a template to apply to your timeline. This will add story beats based on the chosen framework.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 px-8 py-8">
            <div className="space-y-2.5">
              <Select
                value={selectedTemplate || ""}
                onValueChange={setSelectedTemplate}
              >
                <SelectTrigger className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground shadow-sm">
                  <SelectValue placeholder="Select a storytelling template" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                  <SelectItem value="hero">Hero's Journey</SelectItem>
                  <SelectItem value="savecat">Save the Cat</SelectItem>
                  <SelectItem value="threeact">Three-Act Structure</SelectItem>
                  <SelectItem value="storycircle">Story Circle</SelectItem>
                  <SelectItem value="sevenpoint">Seven Point Story Structure</SelectItem>
                  <SelectItem value="mystery">Mystery/Detective</SelectItem>
                  <SelectItem value="romance">Romance Arc</SelectItem>
                  <SelectItem value="romancingbeat">Romancing the Beat</SelectItem>
                  <SelectItem value="snowflake">Snowflake Method</SelectItem>
                  <SelectItem value="storygenius">Story Genius Method</SelectItem>
                  <SelectItem value="fichtean">Fichtean Curve</SelectItem>
                  <SelectItem value="shortjourney">Hero's Journey for Short Stories</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && (
              <>
                <TimelinePreview
                  timelineDetails={convertStoryTemplateToTimelineDetails(STORY_TEMPLATES[selectedTemplate])}
                />
                <Alert className="mt-3 py-2 border-border/40 bg-muted/20">
                  <AlertTitle className="text-xs font-medium">Important Note</AlertTitle>
                  <AlertDescription className="text-xs text-muted-foreground">
                    Applying a template will add story beat cards to your existing chapters.
                    If you don't have enough chapters, new ones will be created automatically.
                    Existing cards will not be modified.
                  </AlertDescription>
                </Alert>
              </>
            )}
          </div>

          <DialogFooter className="p-6 bg-muted/5 border-t border-border/40">
            <Button 
                variant="ghost" 
                onClick={() => onOpenChange(false)}
                className="min-w-[100px] rounded-xl hover:bg-muted/50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyTemplate}
              disabled={!selectedTemplate || isApplying}
              className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base"
            >
              {isApplying ? 'Applying...' : 'Apply Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </>
  );
};